import React, { useState, useEffect } from "react";
import { PickupsResponseContentModel } from "../../../app/data/pickup/models";
import shipperIcon from "../../../images/bol_scan_4.svg";
import consigneeIcon from "../../../images/bol_scan_2.svg";
import phoneIcon from "../../../images/bol_scan_3.svg";
import driverIcon from "../../../images/bol_scan_5.svg";
import originIcon from "../../../images/bol_scan_6.svg";
import destinationIcon from "../../../images/bol_scan_7.svg";
import itemsIcon from "../../../images/bol_scan_8.svg";
import weightIcon from "../../../images/bol_scan_9.svg";
import PickupDetails from "../../../features/common/pickup-details/pickupDetails";
import { getPickupDetails } from "services/pickups";
import { getDriverById } from "slices/pickup/pickupAssignmentSlice";
import { useDispatch } from "react-redux";

interface PickupDocViewerInfoProps {
  pickup: PickupsResponseContentModel | null;
}

interface ServiceCenter {
  airportCode: string;
  terminalName: string;
  terminalNumber: number;
}

const formatTerminal = (sc: ServiceCenter) => (
  <>
    {sc?.airportCode} - {sc?.terminalName?.toUpperCase()}{" "}
    <span className="xgs-pickup-doc-viewer-info__terminal-number">{sc?.terminalNumber}</span>
  </>
);

const SplitInfoRow: React.FC<{
  icon: string;
  iconAlt: string;
  label: string;
  value: React.ReactNode;
}> = ({ icon, iconAlt, label, value }) => (
  <div className="xgs-pickup-doc-viewer-info__row xgs-pickup-doc-viewer-info__row--split">
    <div className="xgs-pickup-doc-viewer-info__row-label-group">
      <img className="xgs-pickup-doc-viewer-info__icon" src={icon} alt={iconAlt} />
      <span className="xgs-pickup-doc-viewer-info__label">{label}</span>
    </div>
    <span className="xgs-pickup-doc-viewer-info__value">{value}</span>
  </div>
);

const InlineInfoRow: React.FC<{
  icon: string;
  iconAlt: string;
  label: string;
  value: React.ReactNode;
}> = ({ icon, iconAlt, label, value }) => (
  <div className="xgs-pickup-doc-viewer-info__row xgs-pickup-doc-viewer-info__row--inline">
    <img className="xgs-pickup-doc-viewer-info__icon" src={icon} alt={iconAlt} />
    <span className="xgs-pickup-doc-viewer-info__inline-text">
      <span className="xgs-pickup-doc-viewer-info__label">{label}</span>
      {' '}{value}
    </span>
  </div>
);

export const PickupDocViewerInfo: React.FC<PickupDocViewerInfoProps> = ({ pickup }) => {
  const dispatch = useDispatch();
  const [showPickupDetails, setShowPickupDetails] = useState(false);
  const [driverName, setDriverName] = useState<string>('');

  useEffect(() => {

    dispatch(
      getDriverById(
        pickup?.pickup.driverId || 0,
        (driverName) => setDriverName(driverName),
        true
      )
    );
  }, [pickup, dispatch, driverName]);

  const pickupDetails = () => {
    if (!pickup) return null;
    const pickupDetails = getPickupDetails(pickup);
    return pickupDetails ? { ...pickupDetails, driverName: driverName } : null;
  }


  return (
    <div className="xgs-pickup-doc-viewer-info">
      <div className="xgs-pickup-doc-viewer-info__header">
        <span className="xgs-pickup-doc-viewer-info__header-label">Pickup #</span>
        <span className="xgs-pickup-doc-viewer-info__header-value"
          style={{ cursor: 'pointer' }} onClick={() => setShowPickupDetails(true)}>
          {' '}{pickup?.pickup?.pickupNumber}
        </span>
      </div>

      <div className="xgs-pickup-doc-viewer-info__body">
        <div className="xgs-pickup-doc-viewer-info__columns">
          <div className="xgs-pickup-doc-viewer-info__column">
            <SplitInfoRow
              icon={shipperIcon}
              iconAlt="Shipper"
              label="Shipper:"
              value={pickup?.shipper?.name}
            />
            <InlineInfoRow
              icon={driverIcon}
              iconAlt="Driver"
              label="Driver:"
              value={driverName || ''}
            />
          </div>
          <div className="xgs-pickup-doc-viewer-info__column">
            <SplitInfoRow
              icon={consigneeIcon}
              iconAlt="Consignee"
              label="Consignee:"
              value={pickup?.consignee?.name}
            />
            <InlineInfoRow
              icon={phoneIcon}
              iconAlt="Phone"
              label="Phone:"
              value={pickup?.pickupFullData?.pickupRequest?.consigneePhone}
            />
          </div>
        </div>

        <div className="xgs-pickup-doc-viewer-info__divider" />

        <div className="xgs-pickup-doc-viewer-info__route">
          <InlineInfoRow
            icon={originIcon}
            iconAlt="Origin SC"
            label="Origin SC:"
            value={formatTerminal(pickup?.originServiceCenter)}
          />
          {!!pickup?.destinationServiceCenter && (
            <>
              <span className="xgs-pickup-doc-viewer-info__route-arrow" aria-hidden="true">→</span>
              <InlineInfoRow
                icon={destinationIcon}
                iconAlt="Destination SC"
                label="Destination SC:"
                value={formatTerminal(pickup?.destinationServiceCenter)}
              />
            </>
          )}
        </div>

        <div className="xgs-pickup-doc-viewer-info__divider" />

        <div className="xgs-pickup-doc-viewer-info__cargo">
          <InlineInfoRow
            icon={itemsIcon}
            iconAlt="Items"
            label="Items:"
            value={pickup?.items?.count}
          />
          <InlineInfoRow
            icon={weightIcon}
            iconAlt="Weight"
            label="Weight:"
            value={<>{pickup?.items?.weight} lbs</>}
          />
        </div>
      </div>
      <PickupDetails
        isOpen={!!showPickupDetails}
        pickup={pickupDetails()}
        onClose={() => {
          setShowPickupDetails(false);
        }}
      />
    </div>
  );
};
